/*
SchedulerIntervalSettings Explanation

Execution: Function call.
Batch: series of executions in short period.


                    `batchSizeLimit` - number of executions for each batch. In this example - 3.
      ______________|_____________                            `executionDelay` - Time before each execution.
     ↓                            ↓                           ↓
[....(---e)......(---e)......(---e)] _ _ _ _ _ _ _ _ _ [....(---e)......(---e)......(---e)] _ _ _ _ _ _ _ _ _
  ↑                     ↑                        ↑
  |                     |                        `batchSizeLimitResetDelay` - Time that needed to pass between batches.
  |                     `executionsInterval`. Time that needed to pass between executions.
  `firstExecutionDelay` - Delay before first execution.


*/


export type SchedulerMeta = {
    initTime?: number,
    executedTimes?: number,
    lastExecutionTime?: number
}

export type SchedulerIntervalSettings = {
    firstExecutionDelay?: number,
    executionDelay?: number,
    executionsInterval: number,
    batchSizeLimitResetDelay?: number,
    batchSizeLimit: number,
}

export type SchedulerParams = {
    schedulerGroup?: string,
    prefix?: string,
    metaPrefix?: string,
    callback: () => void,
    intervalSettings: SchedulerIntervalSettings,
    isExecutionAllowed?: () => boolean,
    onSuccess?: () => void
}

export type SchedulerFactory = (props: SchedulerParams) => () => void

export const getScheduledFunction: SchedulerFactory = (props: SchedulerParams) => {
    const {
        schedulerGroup,
        prefix = "exec-",
        metaPrefix = "exec-meta-",
        callback,
        intervalSettings: {
            firstExecutionDelay = 0,
            executionDelay = 0,
            executionsInterval,
            batchSizeLimitResetDelay = 0,
            batchSizeLimit,
        },
        isExecutionAllowed,
        onSuccess
    } = props

    return () => {
        try {
            const schedulerIdentity = schedulerGroup
            const now = Date.now()



            const saveSchedulerMeta = (schedulerMeta: SchedulerMeta) => {
                localStorage.setItem(metaPrefix + schedulerIdentity, JSON.stringify(schedulerMeta))
            }

            const canExecute: ()=>boolean = () => {
                const schedulerMeta: SchedulerMeta = JSON.parse(String(localStorage.getItem(metaPrefix + schedulerIdentity))) || {}
                let executedTimes = schedulerMeta?.executedTimes || 0
                let canExecuteFlag = true
                const init = schedulerMeta?.initTime || 0

                const executeOnce = batchSizeLimit == 1 && !batchSizeLimitResetDelay;
                if (executeOnce && (localStorage.getItem(prefix + schedulerIdentity) || executedTimes > 0)) {
                    return false;
                }

                if (isExecutionAllowed && !isExecutionAllowed()) {
                    return false;
                }

                // Executions limit handle
                if (batchSizeLimit > 0 && executedTimes >= batchSizeLimit) {
                    const lastExec = schedulerMeta.lastExecutionTime || init

                    if (batchSizeLimitResetDelay && lastExec && lastExec + batchSizeLimitResetDelay < now) {
                        schedulerMeta.executedTimes = 0
                        saveSchedulerMeta(schedulerMeta)
                    } else {
                        return false
                    }
                }

                // Adding delay before first open
                if (!init && firstExecutionDelay && firstExecutionDelay > 0) {
                    schedulerMeta.initTime = now
                    saveSchedulerMeta(schedulerMeta)
                    return false;
                }

                // Handling first delay
                if (init && executedTimes == 0) {
                    if (init + firstExecutionDelay > now) {
                        return false;
                    }
                }

                // Executing after interval
                if (init && (init + executionsInterval < now)) {
                    schedulerMeta.initTime = now
                } else if (init && !(init + executionsInterval < now)) (
                    canExecuteFlag = false
                )

                return canExecuteFlag
            }

            const execute = () => {
                if(canExecute()) {
                    const schedulerMeta: SchedulerMeta = JSON.parse(String(localStorage.getItem(metaPrefix + schedulerIdentity))) || {}
                    schedulerMeta.executedTimes = (schedulerMeta.executedTimes || 0) + 1
                    schedulerMeta.lastExecutionTime = now
                    saveSchedulerMeta(schedulerMeta)
                    callback()

                    try {
                        if (onSuccess) onSuccess()
                    } catch (err) {
                        console.error(err)
                    }
                }
            }

            if (canExecute()) {
                setTimeout(execute, executionDelay)
            }

        } catch (e) {
            console.error(e)
        }
    }
}